// External libraries
import * as React from 'react';
import { inject, observer } from 'mobx-react';

import { withLocalization } from 'bernie-l10n';

import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

// Types
import { ShareEvents, ShareMenuProps, ShareMenuState } from './types';

// UITK
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { Level, liveAnnounce } from 'uitk-react-utils';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkMenu, UitkMenuContainer, UitkMenuTrigger } from 'uitk-react-menu';
import { UitkSecondaryButton } from 'uitk-react-button';

// Components
import { asyncCopyToClipboard } from 'utils/clipboard-utils';

// Utils
import { ShareMenuItem } from './share-menu-item';

@withLocalization
@inject('uiPage', 'analytics', 'routeStore', 'currentHotel', 'context')
@observer
export class ShareMenu extends React.Component<ShareMenuProps, ShareMenuState> {
  private socialShareWindowDimensions = {
    width: 550,
    height: 400,
  };

  constructor(props: ShareMenuProps) {
    super(props);
    this.state = {
      isShareMenuOpen: false,
      hotelURL: '',
    };

    this.shareMenuTrigger = this.shareMenuTrigger.bind(this);
    this.copyTextToClickBoard = this.copyTextToClickBoard.bind(this);
    this.onClickFB = this.onClickFB.bind(this);
    this.trackEvent = this.trackEvent.bind(this);
    this.onClickTwitter = this.onClickTwitter.bind(this);
    this.onClickEmail = this.onClickEmail.bind(this);
  }

  componentDidMount() {
    // The MCICID param is added to every shared link to track the traffic that comes from URLs shared by users
    this.props.routeStore?.updateSearch({ MCICID: undefined }, {}, true);

    // Since window object doesn't exist on the server, we need to load the current hotel URL after app is mounted on the client
    this.setState({ hotelURL: `${window.location.href}${window.location.search ? '&MCICID=' : '?MCICID='}` });
  }

  private async shareMenuTrigger() {
    // Share API is mobile only. So let's make sure the compatibility of the browser
    if (navigator.share) {
      this.trackEvent(ShareEvents.SHARE_MOBILE_NATIVE, true);

      // Opens native mobile share dialog
      navigator.share({
        text: this.props.currentHotel.shareInfo.sharedText,
        url: `${this.state.hotelURL}${ShareEvents.SHARE_MOBILE_NATIVE}`,
      });
    } else {
      // If Share API is not supported (usually because we are on Desktop or Firefox Mobile) let's use the share dialog
      this.setState(
        state => ({ isShareMenuOpen: !state.isShareMenuOpen }),
        () => {
          if (this.state.isShareMenuOpen) {
            this.trackEvent(ShareEvents.OPEN_MENU);
          } else {
            this.trackEvent(ShareEvents.CLOSE_MENU);
          }
        }
      );
    }
  }

  /**
   * Sends a track event
   * @param {ShareEvents} shareEvent - Event type to track
   * @param {boolean} trackImmediate
   */
  private trackEvent(shareEvent: ShareEvents, trackImmediate: boolean = false) {
    const analyticsTrackingInfo = {
      rfrr: 'Share-Button.',
      linkName: 'Share Button Event: ',
    };

    switch (shareEvent) {
      case ShareEvents.OPEN_MENU:
        analyticsTrackingInfo.rfrr += ShareEvents.OPEN_MENU;
        analyticsTrackingInfo.linkName += 'Share Menu Opened';
        break;
      case ShareEvents.CLOSE_MENU:
        analyticsTrackingInfo.rfrr += ShareEvents.CLOSE_MENU;
        analyticsTrackingInfo.linkName += 'Share Menu Closed';
        break;
      case ShareEvents.SHARE_COPY_LINK:
        analyticsTrackingInfo.rfrr += ShareEvents.SHARE_COPY_LINK;
        analyticsTrackingInfo.linkName += 'Link Copied';
        break;
      case ShareEvents.SHARE_EMAIL:
        analyticsTrackingInfo.rfrr += ShareEvents.SHARE_EMAIL;
        analyticsTrackingInfo.linkName += 'Shared by Email';
        break;
      case ShareEvents.SHARE_FACEBOOK:
        analyticsTrackingInfo.rfrr += ShareEvents.SHARE_FACEBOOK;
        analyticsTrackingInfo.linkName += 'Shared by Facebook';
        break;
      case ShareEvents.SHARE_MOBILE_NATIVE:
        analyticsTrackingInfo.rfrr += ShareEvents.SHARE_MOBILE_NATIVE;
        analyticsTrackingInfo.linkName += 'Shared using mobile native OS dialog';
        break;
      case ShareEvents.SHARE_TWITTER:
        analyticsTrackingInfo.rfrr += ShareEvents.SHARE_TWITTER;
        analyticsTrackingInfo.linkName += 'Shared by Twitter';
        break;
      default:
        break;
    }

    this.props.analytics.trackEvent(`${analyticsTrackingInfo.rfrr}.click`, analyticsTrackingInfo.linkName, null, null, trackImmediate);
  }

  private async copyTextToClickBoard() {
    await asyncCopyToClipboard(`${this.state.hotelURL}${ShareEvents.SHARE_COPY_LINK}`);
    const { uiPage } = this.props;
    if (uiPage && typeof uiPage.showNotification === 'function') {
      const linkCopiedText = this.props.currentHotel.shareInfo.linkCopiedMessage;
      // Let's user knows the text was copied
      uiPage.showNotification(linkCopiedText);

      // ARIA live region to announce link was copied to screen readers
      liveAnnounce(linkCopiedText, Level.POLITE);
    }
    this.trackEvent(ShareEvents.SHARE_COPY_LINK);
  }

  /**
   * When Facebook button is pressed the the share Facebook dialog is open
   * @see https://developers.facebook.com/docs/workplace/sharing/share-dialog/
   * @see https://scotch.io/tutorials/how-to-share-webpages-with-facebook
   */
  private onClickFB() {
    const facebookShareUrl = `${this.state.hotelURL}${ShareEvents.SHARE_FACEBOOK}`;

    // Opens a new window making a GET request to facebook's share endpoint
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(facebookShareUrl)}&quote=${encodeURIComponent(
        `${this.props.currentHotel.shareInfo.sharedText} ${facebookShareUrl}`
      )}`,
      'facebook-share-dialog',
      `width=${this.socialShareWindowDimensions.width},height=${this.socialShareWindowDimensions.height}`
    );

    this.trackEvent(ShareEvents.SHARE_FACEBOOK);
  }

  /**
   * When twitter button is pressed opens a new window making a GET request to Twitter share endpoint
   * @see https://developer.twitter.com/en/docs/twitter-for-websites/tweet-button/overview
   */
  private onClickTwitter() {
    // Opens a new window making a GET request to twitter's share endpoint
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `${this.props.currentHotel.shareInfo.sharedText} ${this.state.hotelURL}${ShareEvents.SHARE_TWITTER}`
      )}`,
      'twitter-share-dialog',
      `width=${this.socialShareWindowDimensions.width},height=${this.socialShareWindowDimensions.height}`
    );

    this.trackEvent(ShareEvents.SHARE_TWITTER);
  }

  private onClickEmail() {
    this.trackEvent(ShareEvents.SHARE_EMAIL, true);
  }

  render() {
    const { isShareMenuOpen } = this.state;

    if (!this.props.currentHotel.shareInfo) {
      return null;
    }

    return (
      <Viewport>
        <ViewSmall>
          <UitkLayoutFlex className="share-or-save l-x-padding-four xl-x-padding-four">
            <UitkLayoutFlexItem>
              <UitkMenu isOpen={isShareMenuOpen} onTriggerClick={this.shareMenuTrigger}>
                <UitkMenuTrigger onClick={this.shareMenuTrigger}>
                  <button className="uitk-link uitk-link-layout-default uitk-type-300" data-stid="share-button" type="button">
                    <UitkIcon name="share" />
                    {this.props.currentHotel.shareInfo.menuName}
                  </button>
                </UitkMenuTrigger>
                <UitkMenuContainer autoPosition={true}>
                  <div role="menu" className="uitk-list uitk-type-400" data-stid="share-menu">
                    <ShareMenuItem onClick={this.onClickFB} openNewWindow={true}>
                      {this.props.currentHotel.shareInfo.linkList[0].value}
                    </ShareMenuItem>
                    <ShareMenuItem onClick={this.onClickTwitter} openNewWindow={true}>
                      {this.props.currentHotel.shareInfo.linkList[1].value}
                    </ShareMenuItem>
                    <ShareMenuItem onClick={this.copyTextToClickBoard} openNewWindow={false}>
                      {this.props.currentHotel.shareInfo.linkList[3].value}
                    </ShareMenuItem>
                    <ShareMenuItem
                      href={`mailto:?subject=${encodeURIComponent(this.props.currentHotel.shareInfo.sharedText)}&body=${encodeURIComponent(
                        `${this.props.currentHotel.shareInfo.sharedText} ${this.state.hotelURL}${ShareEvents.SHARE_EMAIL}`
                      )}`}
                      target="__blank"
                      rel="noopener noreferrer"
                      onClick={this.onClickEmail}
                      renderAs="a"
                      openNewWindow={true}
                    >
                      {this.props.currentHotel.shareInfo.linkList[2].value}
                    </ShareMenuItem>
                  </div>
                </UitkMenuContainer>
              </UitkMenu>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </ViewSmall>
        <ViewLarge>
          <UitkLayoutFlex className="share-or-save m-r-padding-two l-r-padding-two xl-r-padding-two">
            <UitkLayoutFlexItem>
              <UitkMenu isOpen={isShareMenuOpen} onTriggerClick={this.shareMenuTrigger}>
                <UitkMenuTrigger>
                  <UitkSecondaryButton icon="share" iconSize={UitkIconSize.SMALL} text={this.props.currentHotel.shareInfo.menuName} />
                </UitkMenuTrigger>
                <UitkMenuContainer autoPosition={true}>
                  <div role="menu" className="uitk-list uitk-type-400" data-stid="share-menu">
                    <ShareMenuItem onClick={this.onClickFB} openNewWindow={true}>
                      {this.props.currentHotel.shareInfo.linkList[0].value}
                    </ShareMenuItem>
                    <ShareMenuItem onClick={this.onClickTwitter} openNewWindow={true}>
                      {this.props.currentHotel.shareInfo.linkList[1].value}
                    </ShareMenuItem>
                    <ShareMenuItem onClick={this.copyTextToClickBoard} openNewWindow={false}>
                      {this.props.currentHotel.shareInfo.linkList[3].value}
                    </ShareMenuItem>
                    <ShareMenuItem
                      href={`mailto:?subject=${encodeURIComponent(this.props.currentHotel.shareInfo.sharedText)}&body=${encodeURIComponent(
                        `${this.props.currentHotel.shareInfo.sharedText} ${this.state.hotelURL}${ShareEvents.SHARE_EMAIL}`
                      )}`}
                      target="__blank"
                      rel="noopener noreferrer"
                      onClick={this.onClickEmail}
                      renderAs="a"
                      openNewWindow={true}
                    >
                      {this.props.currentHotel.shareInfo.linkList[2].value}
                    </ShareMenuItem>
                  </div>
                </UitkMenuContainer>
              </UitkMenu>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </ViewLarge>
      </Viewport>
    );
  }
}
